import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import Lenis from "lenis";
import axios from "axios";
import { ImmediateHelp } from "@/components/ImmediateHelp";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";
import OurWhy from "@/pages/OurWhy";
import CareMap from "@/pages/CareMap";
import Gatherings from "@/pages/Gatherings";
import Stories from "@/pages/Stories";
import GetInvolved from "@/pages/GetInvolved";
import Give from "@/pages/Give";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AnnualReports from "@/pages/AnnualReports";
import CommitmentToCare from "@/pages/CommitmentToCare";
import PrayerTeam from "@/pages/PrayerTeam";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

const SmoothScroll = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
    };
  }, []);
  return null;
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    const exchange = async () => {
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        navigate("/prayer-team", { state: { user: data }, replace: true });
      } catch (e) {
        const msg = e.response?.data?.detail;
        navigate("/prayer-team", { state: { authError: typeof msg === "string" ? msg : "Sign-in failed" }, replace: true });
      }
    };
    exchange();
  }, [navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-espresso">
      <p className="font-garamond text-2xl italic text-parchment/70">Signing you in…</p>
    </div>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/our-why" element={<OurWhy />} />
      <Route path="/care-map" element={<CareMap />} />
      <Route path="/gatherings" element={<Gatherings />} />
      <Route path="/stories" element={<Stories />} />
      <Route path="/get-involved" element={<GetInvolved />} />
      <Route path="/give" element={<Give />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/annual-reports" element={<AnnualReports />} />
      <Route path="/commitment-to-care" element={<CommitmentToCare />} />
      <Route path="/prayer-team" element={<PrayerTeam />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );
};
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SmoothScroll />
        <ScrollManager />
        <div className="sticky top-0 z-50">
          <ImmediateHelp />
          <Navbar />
        </div>
        <AppRoutes />
        <Footer />
        <Toaster theme="dark" position="bottom-center" />
      </BrowserRouter>
    </div>
  );
}

export default App;
